import { Copyright } from '@c/Content/Copyright'
import { UserAgreement } from '@c/Content/UserAgreement'
import cns from 'classnames'
import { RefObject, useEffect, useMemo, useRef, useState } from 'react'
import ReactGA from 'react-ga4'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import CategoryFilter from '@/components/Catalog/CategoryFilter'
import { useAppDispatch } from '@/core/store'
import { useAppSelector } from '@/core/store'
import { setModal } from '@/core/store/ui.store'

const Header = () => {
  const { t } = useTranslation('header')
  const dispatch = useAppDispatch()

  const [menuShown, setMenuShown] = useState(false)
  const [navMenuOpen, setMenuOpen] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [agreement, setAgreement] = useState(false)
  const [copyright, setCopyright] = useState(false)

  const filterModalRef: RefObject<HTMLDivElement> = useRef(null)
  const navMenuRef: RefObject<HTMLDivElement> = useRef(null)

  const { isFilterChanged } = useAppSelector((store) => store.catalogStore)

  const pages = useMemo(() => {
    return [{ slug: '#', modal: 'feedback', label: 'nav3' }]
  }, [])
  const url = window.location.pathname.split('/').pop()

  useEffect(() => {
    // console.log(url)
    if (url == 'map') setFilterOpen(true)
  }, [url])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterOpen &&
        filterModalRef.current &&
        !filterModalRef.current.contains(event.target as Node)
      ) {
        setFilterOpen(false)
      }

      if (navMenuOpen && navMenuRef.current && !navMenuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [filterOpen, navMenuOpen])

  const toggleFilterModal = (event: React.MouseEvent) => {
    event.stopPropagation()
    setFilterOpen((prevState) => !prevState)
    setMenuOpen(false) // Close the nav menu if it's open
    ReactGA.event({
      category: 'click',
      action: filterOpen ? 'FilterMenuClose' : 'FilterMenuOpen',
      label: filterOpen ? 'close_filter' : 'open_filter',
    })
  }

  const toggleNavMenu = (event: React.MouseEvent) => {
    event.stopPropagation()
    setMenuOpen((prevState) => !prevState)
    setFilterOpen(false) // Close the filter modal if it's open
    ReactGA.event({
      category: 'click',
      action: navMenuOpen ? 'MenuClose' : 'MenuOpen',
      label: navMenuOpen ? 'close_menu' : 'open_menu',
    })
  }

  const handleFilterApply = () => {
    setFilterOpen(false)
  }

  // console.log(useLocation().pathname)

  return (
    <>
      <UserAgreement active={agreement} onClose={() => setAgreement(false)} />
      <Copyright active={copyright} onClose={() => setCopyright(false)} />
      <nav className={'header'}>
        <Link to={'https://worldmuseum.online/'}>
          {location.pathname !== '/' && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="175"
              height="31"
              viewBox="0 0 175 31"
              fill="none"
            >
              <path
                d="M53.5089 8.525H56.1032L51.558 23.25H48.9637L45.6431 12.2694L42.3224 23.25H39.7281L35.2036 8.525H37.7772L41.0771 20.4312L44.6053 8.525H46.7015L50.2298 20.4312L53.5089 8.525Z"
                fill="#d58010"
              />
              <path
                d="M56.1322 18.2014C56.1322 14.7095 58.7473 12.8794 61.5284 12.8794C64.3302 12.8794 66.9245 14.7095 66.9245 18.2014C66.9245 21.6934 64.3302 23.5235 61.5284 23.5235C58.7473 23.5235 56.1322 21.6934 56.1322 18.2014ZM64.6623 18.2014C64.6623 16.2241 63.3755 14.9409 61.5284 14.9409C59.6605 14.9409 58.3737 16.2241 58.3737 18.2014C58.3737 20.1788 59.6605 21.462 61.5284 21.462C63.3755 21.462 64.6623 20.1788 64.6623 18.2014Z"
                fill="#d58010"
              />
              <path
                d="M71.0494 14.8147C71.8173 13.5736 73.4361 12.7742 75.2418 12.9425V15.1512C73.4569 14.8988 71.9003 15.6982 71.2984 17.1286V23.25H69.0777V13.1529H71.0494V14.8147Z"
                fill="#d58010"
              />
              <path d="M76.9629 7.89393H79.1836V23.25H76.9629V7.89393Z" fill="#d58010" />
              <path
                d="M89.916 8.525H92.1367V23.25H90.1443V21.6513C89.3764 22.64 88.1934 23.5235 86.3255 23.5235C83.3161 23.5235 81.386 21.1044 81.386 18.2014C81.386 15.2985 83.3161 12.8794 86.3255 12.8794C88.0689 12.8794 89.2311 13.6577 89.916 14.4571V8.525ZM89.8953 16.9603C89.4179 15.8665 88.2764 14.8567 86.7198 14.8567C84.935 14.8567 83.6275 16.2241 83.6275 18.2014C83.6275 20.1788 84.935 21.5461 86.7198 21.5461C88.2764 21.5461 89.4179 20.5364 89.8953 19.4215V16.9603Z"
                fill="#d58010"
              />
              <path
                d="M108.472 8.525H110.609V23.25H108.244V12.9425L103.657 19.9264H102.246L97.6588 12.9215V23.25H95.2928V8.525H97.4305L102.951 16.9603L108.472 8.525Z"
                fill="#d58010"
              />
              <path
                d="M120.991 13.1529H123.212V23.25H121.24V21.8616C120.514 22.7451 119.289 23.5235 117.774 23.5235C115.138 23.5235 113.665 21.7985 113.665 19.4005V13.1529H115.885V19.106C115.885 20.7467 116.736 21.5251 118.21 21.5251C119.476 21.5251 120.43 20.6626 120.991 19.8422V13.1529Z"
                fill="#d58010"
              />
              <path
                d="M125.471 15.8875C125.471 13.6787 127.463 12.8794 129.435 12.8794C130.805 12.8794 132.05 13.237 133.026 13.7419V15.7192C132.195 15.1512 130.763 14.7305 129.58 14.7305C128.688 14.7305 127.733 15.004 127.733 15.8244C127.733 17.7807 133.731 16.8762 133.731 20.5574C133.731 22.7872 131.697 23.5235 129.435 23.5235C127.982 23.5235 126.509 23.1659 125.533 22.5558V20.5574C126.571 21.2516 128.086 21.6302 129.331 21.6302C130.577 21.6302 131.49 21.4199 131.49 20.5995C131.49 18.7273 125.471 19.5056 125.471 15.8875Z"
                fill="#d58010"
              />
              <path
                d="M135.116 18.2014C135.116 14.5412 137.856 12.8794 140.159 12.8794C144.123 12.8794 145.493 16.1189 144.85 19.0429H137.358C137.607 20.8519 139.35 21.6092 141.176 21.6092C142.38 21.6092 143.48 21.3147 144.331 20.894V22.8293C143.418 23.271 142.152 23.5235 140.761 23.5235C137.71 23.5235 135.116 21.7565 135.116 18.2014ZM142.857 17.36C142.982 16.4765 142.442 14.7516 140.263 14.7516C138.769 14.7516 137.42 15.6561 137.337 17.36H142.857Z"
                fill="#d58010"
              />
              <path
                d="M154.331 13.1529H156.552V23.25H154.581V21.8616C153.854 22.7451 152.63 23.5235 151.115 23.5235C148.479 23.5235 147.005 21.7985 147.005 19.4005V13.1529H149.226V19.106C149.226 20.7467 150.077 21.5251 151.55 21.5251C152.816 21.5251 153.771 20.6626 154.331 19.8422V13.1529Z"
                fill="#d58010"
              />
              <path
                d="M161.551 23.25H159.331V13.1529H161.302V14.415C161.842 13.805 163.004 12.8794 164.623 12.8794C166.117 12.8794 167.155 13.5105 167.736 14.4991C168.608 13.6156 169.77 12.8794 171.223 12.8794C173.859 12.8794 175 14.8567 175 16.9393V23.25H172.779V17.0024C172.779 15.6982 172.011 14.8778 170.808 14.8778C169.708 14.8778 168.857 15.614 168.338 16.2451C168.359 16.4765 168.379 16.7079 168.379 16.9393V23.25H166.159V17.0024C166.159 15.6982 165.391 14.8778 164.187 14.8778C162.983 14.8778 162.07 15.7613 161.551 16.4344V23.25Z"
                fill="#d58010"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13.006 31L13.9565 30.2026C21.9493 23.4972 26.012 17.6423 26.012 12.4958C26.012 5.20749 20.0913 0 13.006 0C5.92069 0 0 5.20749 0 12.4958C0 17.6423 4.06265 23.4972 12.0555 30.2026L13.006 31ZM13.006 28.147C5.60695 21.9809 1.97061 16.7559 1.97061 12.6024C1.97061 6.62068 6.95879 1.60618 13.006 1.60618C19.0532 1.60618 24.0414 6.62068 24.0414 12.6024C24.0414 16.7559 20.405 21.9809 13.006 28.147Z"
                fill="#d58010"
              />
              <path
                d="M6.92334 15.8875C6.92334 17.3148 8.06854 18.4062 9.5661 18.4062H10.447V13.3687H9.5661C8.94946 13.3687 8.42091 13.5367 7.98045 13.8725C7.09953 10.5981 9.6542 7.49167 13.0898 7.49167C16.5254 7.49167 19.08 10.5981 18.1991 13.8725C17.7587 13.5367 17.2301 13.3687 16.6135 13.3687H15.7325V18.4062H16.6135C18.111 18.4062 19.2562 17.3148 19.2562 15.8875V15.8035C21.899 11.2698 18.4634 5.8125 13.0898 5.8125C7.71617 5.8125 4.36867 11.2698 6.92334 15.8035V15.8875Z"
                fill="#d58010"
              />
            </svg>
          )}
        </Link>
        <div className="header__nav">
          {useLocation().pathname == '/map' && (
            <button onClick={toggleFilterModal} className="burger_btn">
              <img className="filter__img" src="/img/new/base.png" alt="Base icon" />
              {/* <svg
                id={'noClose'}
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="18"
                viewBox="0 0 20 18"
                fill="none"
              >
                <path
                  id={'noClose'}
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M11 8C9.13616 8 7.57006 6.72523 7.12602 5H0V3H7.12602C7.57006 1.27477 9.13616 0 11 0C13.2091 0 15 1.79086 15 4C15 6.20914 13.2091 8 11 8ZM17 3H20V5H17V3ZM6 18C4.13616 18 2.57006 16.7252 2.12602 15H0V13H2.12602C2.57006 11.2748 4.13616 10 6 10C8.20914 10 10 11.7909 10 14C10 16.2091 8.20914 18 6 18ZM12 15H20V13H12V15ZM8 14C8 15.1046 7.10457 16 6 16C4.89543 16 4 15.1046 4 14C4 12.8954 4.89543 12 6 12C7.10457 12 8 12.8954 8 14ZM13 4C13 5.10457 12.1046 6 11 6C9.89543 6 9 5.10457 9 4C9 2.89543 9.89543 2 11 2C12.1046 2 13 2.89543 13 4Z"
                  fill={isFilterChanged ? '#FF0000' : filterOpen ? '#FD8108' : '#2B3537'}
                />
              </svg> */}
            </button>
          )}
          <button onClick={toggleNavMenu} className="burger_btn">
            <svg
              id={'noClose'}
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <rect
                id={'noClose'}
                x="2"
                y="5"
                width="20"
                height="2"
                fill={navMenuOpen ? '#FD8108' : '#2B3537'}
              />
              <rect
                id={'noClose'}
                x="2"
                y="11"
                width="20"
                height="2"
                fill={navMenuOpen ? '#FD8108' : '#2B3537'}
              />
              <rect
                id={'noClose'}
                x="2"
                y="17"
                width="20"
                height="2"
                fill={navMenuOpen ? '#FD8108' : '#2B3537'}
              />
            </svg>
          </button>

          {filterOpen && (
            <>
              <div ref={filterModalRef} className={'filter_modal'}>
                <CategoryFilter onApply={handleFilterApply} />
              </div>
              <div className="filter_description">
                <div className="filter_description_content">
                  <p>
                    <strong>On the map you can:</strong>
                  </p>
                  <p>
                    <strong>Discover nearby:</strong> See on the map the most fascinating landmarks
                    located right near you. Whether you are seeking iconic monuments or hidden gems,
                    exploring must see places in Rome becomes effortless with our service.
                  </p>
                  <p>
                    <strong>Dive into history:</strong> Enjoy captivating stories about any of the
                    450 carefully selected sites, described in detail by AI and our experts. Embark
                    on a Rome walking tour and immerse yourself in the culture and history that
                    define this eternal city.
                  </p>
                </div>
              </div>
            </>
          )}

          {navMenuOpen && (
            <div ref={navMenuRef} className={'nav__menu'}>
              <Link
                to={'https://worldmuseum.online/'}
                onClick={() => {
                  setMenuOpen(false)
                  ReactGA.event({
                    category: 'click',
                    action: 'MenuClick',
                    label: 'go_to_homepage',
                  })
                }}
              >
                Home page
              </Link>
              <Link
                onClick={() => {
                  dispatch(setModal({ name: 'feedback' }))
                  setMenuOpen(false)
                  ReactGA.event({
                    category: 'click',
                    action: 'MenuClick',
                    label: 'open_feedback',
                  })
                }}
                to="#feedback"
              >
                Feedback
              </Link>
              {/* <Link
                onClick={() => {
                  setAgreement(true)
                  setMenuOpen(false)
                  ReactGA.event({
                    category: 'click',
                    action: 'MenuClick',
                    label: 'open_user_agreement',
                  })
                }}
                to="#user-agreement"
              >
                User agreement
              </Link> */}
              {/* <Link
                onClick={() => {
                  setCopyright(true)
                  setMenuOpen(false)
                  ReactGA.event({
                    category: 'click',
                    action: 'MenuClick',
                    label: 'open_copyright',
                  })
                }}
                to={'/map'}
              >
                Copyright
              </Link> */}
            </div>
          )}
        </div>
      </nav>

      <ul className={cns('sidenav', menuShown && '_active')} id="sidenav">
        {pages.map((page, idx) => (
          <li key={idx} onClick={() => setMenuShown(false)}>
            {page.modal ? (
              <a href={page.slug} onClick={() => dispatch(setModal({ name: page.modal }))}>
                {t(page.label)}
              </a>
            ) : (
              <Link to={page.slug}>{t(page.label)}</Link>
            )}
          </li>
        ))}
      </ul>

      <div
        className={cns('sidenav-overlay', menuShown && '_active')}
        onClick={() => setMenuShown(false)}
      />
    </>
  )
}

export default Header
